'use client'
import { useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import { MdChevronLeft, MdChevronRight } from 'react-icons/md'
import 'swiper/css'
import 'swiper/css/navigation'
import LinkComponent  from '@components/LinkComponent'
import AgentCard from '@components/AgentCard'

// Same chevron-in-a-circle nav treatment as Gallery.jsx's PopupRows/PopupCols —
// used here instead of Swiper's own default `navigation` buttons, which render
// as small, unstyled arrows that are easy to miss and don't match the rest of
// the site's UI.
function NavButton({ innerRef, direction, disabled }) {
  const isPrev = direction === 'prev'
  return (
    <button
      ref={innerRef}
      type="button"
      aria-label={isPrev ? 'Previous' : 'Next'}
      disabled={disabled}
      className={`absolute top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/80 text-white rounded-full p-1.5 disabled:opacity-0 disabled:pointer-events-none transition-all ${isPrev ? 'left-2' : 'right-2'}`}
    >
      {isPrev ? <MdChevronLeft size={22} /> : <MdChevronRight size={22} />}
    </button>
  )
}

const AgentCardSlider = ({ title, description, titleColor, titleSize, titleWeight, titleAlign, subtitleColor, subtitleSize, agentSlider, agents, url, cardStyle, dictionary, lang }) => {

  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])

  const prevRef = useRef(null)
  const nextRef = useRef(null)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  // NEW: title/description previously had no way to be recolored — only a
  // fixed CSS class was applied. These inline styles layer on top of that
  // class (color/size/weight/align only), so untouched sections still pick
  // up the class's own defaults via `undefined` falling through.
  const titleStyle = {
    color:     titleColor  || undefined,
    fontSize:  titleSize   || undefined,
    fontWeight: titleWeight || undefined,
    textAlign: titleAlign  || undefined,
  }
  const descStyle = {
    color:    subtitleColor || undefined,
    fontSize: subtitleSize  || undefined,
  }

  if (!mounted) {
    const maxSlides =
      agentSlider?.agentSlider?.desktop ||
      agentSlider?.agentSlider?.tablet  ||
      Math.ceil(agentSlider?.agentSlider?.mobile || 1.2) ||
      3
    const limited = agents.slice(0, maxSlides)
    return (
      <section className='carousel-wrapper lg:pl-0 pl-3'>
        {url ? (
          <LinkComponent href={url}>
            <h2 className='carousel-title-align' style={titleStyle}>{title?.[lang]}</h2>
          </LinkComponent>
        ) : (
          <h2 className='carousel-title-align' style={titleStyle}>{title?.[lang]}</h2>
        )}
        {description?.[lang]?.trim() && (
          <p className='carousel-description-align' style={descStyle}>{description[lang]}</p>
        )}
        <div className="flex flex-row" style={{ gap: '16px' }}>
          {limited.map((agent, i) => (
            <AgentCard key={i} agent={agent} cardStyle={cardStyle} dictionary={dictionary?.card} lang={lang} />
          ))}
        </div>
      </section>
    )
  }

  return (
    <section className='carousel-wrapper w-full lg:pl-0 pl-3'>
      {url ? (
        <LinkComponent href={url}>
          <h2 className='carousel-title-align' style={titleStyle}>{title?.[lang]}</h2>
        </LinkComponent>
      ) : (
        <h2 className='carousel-title-align' style={titleStyle}>{title?.[lang]}</h2>
      )}
      {description?.[lang]?.trim() && (
        <p className='carousel-description-align' style={descStyle}>{description[lang]}</p>
      )}
      <div className="relative">
        <Swiper
          spaceBetween={16}
          modules={[Navigation]}
          slidesPerView={agentSlider.agentSlider.mobile || 1.2}
          breakpoints={{
            768:  { slidesPerView: agentSlider.agentSlider.tablet  || 2 },
            1024: { slidesPerView: agentSlider.agentSlider.desktop || 3 },
          }}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = prevRef.current
            swiper.params.navigation.nextEl = nextRef.current
          }}
          navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
          onSlideChange={(swiper) => {
            setIsBeginning(swiper.isBeginning)
            setIsEnd(swiper.isEnd)
          }}
          onSwiper={(swiper) => {
            setIsBeginning(swiper.isBeginning)
            setIsEnd(swiper.isEnd)
          }}
        >
          {agents.map((agent, i) => (
            <SwiperSlide key={i}>
              <AgentCard agent={agent} cardStyle={cardStyle} dictionary={dictionary?.card} lang={lang} />
            </SwiperSlide>
          ))}
        </Swiper>
        <NavButton innerRef={prevRef} direction="prev" disabled={isBeginning} />
        <NavButton innerRef={nextRef} direction="next" disabled={isEnd} />
      </div>
    </section>
  )
}

export default AgentCardSlider